import { useQuery, useInfiniteQuery } from "@tanstack/vue-query";
import type {
    UseQueryOptions,
    UseInfiniteQueryOptions,
} from "@tanstack/vue-query";

export const useVueQuery = <TData = unknown, TError = unknown>(
    queryKey: string,
    queryFn: () => Promise<TData>,
    options?: Omit<UseQueryOptions<TData, TError>, "queryKey" | "queryFn">,
) => {
    const { isPending, isFetching, isError, data, error, isSuccess, refetch } =
        useQuery<TData, TError>({
            queryKey: [queryKey],
            queryFn,
            ...options,
        });

    return {
        isPending,
        isFetching,
        isError,
        data,
        error,
        refetch,
        isSuccess,
    };
};

export const useVueInfiniteQuery = <TData = unknown, TError = unknown>(
    queryKey: string,
    queryFn: (context: { pageParam: unknown }) => Promise<TData>,
    options?: Omit<
        UseInfiniteQueryOptions<TData, TError, TData, readonly unknown[]>,
        "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
    >,
) => {
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
        isPending,
        isError,
    } = useInfiniteQuery<TData, TError, TData, readonly unknown[]>({
        queryKey: [queryKey],
        queryFn,
        initialPageParam: 1,
        getNextPageParam: (lastPage: any) => lastPage.nextPage,
        ...options,
    });

    return {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
        isPending,
        isError,
    };
};
